import { useState } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LocaleProvider, useT } from '../../contexts/LocaleContext';
import FootballOpsHome from './FootballOpsHome';
import FootballOpsTourDetail from './FootballOpsTourDetail';
import { LanguageSwitch } from '../../components/LanguageSwitch';

// Football Operations portal — a read-only view of every tour in the user's
// portals, limited to the football side: team info, fixtures, teams, itineraries
// and tour notes. No sales, finance or admin.
//
// Shown in Spanish by default (the team is mostly Spanish-speaking), with an
// EN / ES switch remembered on this device.

const LOCALE_KEY = 'mst_football_ops_locale';

function savedLocale() {
  try { return localStorage.getItem(LOCALE_KEY) === 'en' ? 'en' : 'es'; } catch { return 'es'; }
}

function Shell({ locale, onLocaleChange }) {
  const { currentUser, logout, isStaffOrAdmin } = useAuth();
  const navigate = useNavigate();
  const t = useT();

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const initials = currentUser?.email?.[0]?.toUpperCase() || '?';
  const displayName = currentUser?.displayName || currentUser?.email || '';

  return (
    <div className="min-h-screen bg-page flex flex-col" lang={locale}>

      {/* Topbar */}
      <header className="h-14 bg-navy flex items-center justify-between gap-2 px-4 flex-shrink-0">
        <button onClick={() => navigate('/football-ops')} className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-[8px] bg-ba flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-[12px] tracking-wide">MST</span>
          </div>
          <span className="hidden md:inline text-white font-semibold text-[15px]">{t('footballOps.portalName')}</span>
          <span className="hidden sm:inline bg-white/15 text-white text-[11px] px-3 py-0.5 rounded-[20px] whitespace-nowrap">
            {t('footballOps.badge')}
          </span>
        </button>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitch locale={locale} onChange={onLocaleChange} />
          {isStaffOrAdmin && (
            <button
              onClick={() => navigate('/staff')}
              className="hidden sm:flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[12px] font-semibold px-3 py-1.5 rounded-[8px] transition"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
              {t('footballOps.opsView')}
            </button>
          )}
          <div className="hidden sm:flex items-center gap-2 bg-white/10 rounded-[20px] px-3 py-1.5">
            <span className="w-6 h-6 rounded-full bg-sg flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0">
              {initials}
            </span>
            <span className="hidden lg:inline text-white text-[13px] max-w-[140px] truncate">{displayName}</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-white/70 hover:text-white text-[13px] transition whitespace-nowrap"
          >
            {t('footballOps.signOut')}
          </button>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 md:px-8 md:py-8 overflow-y-auto page-fade">
        <Routes>
          <Route index element={<FootballOpsHome />} />
          <Route path="tours/:id" element={<FootballOpsTourDetail />} />
        </Routes>
      </main>
    </div>
  );
}

export default function FootballOpsDashboard() {
  const [locale, setLocale] = useState(savedLocale);

  function changeLocale(next) {
    setLocale(next);
    try { localStorage.setItem(LOCALE_KEY, next); } catch {}
  }

  return (
    <LocaleProvider locale={locale}>
      <Shell locale={locale} onLocaleChange={changeLocale} />
    </LocaleProvider>
  );
}
